import {
  User,
  Mail,
  Building2,
  Clock,
  Calendar,
  CreditCard,
} from 'lucide-react'
import { useEmployeeContext } from './EmployeeDetailLayout'
import { formatDate } from '../../utils/dateUtils'
import {
  WORK_CENTER_LABELS,
  SHIFT_LABELS,
  WorkCenterType,
  Shift,
} from '../../types'

export function EmployeeOverviewTab() {
  const {
    employee,
    isEditing,
    formData,
    setFormData,
    positions,
  } = useEmployeeContext()

  if (!employee) return null

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Personal Info */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Información Personal
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              ID Empleado
            </label>
            <p className="font-mono bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 rounded">
              {employee.employeeId}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              DNI
            </label>
            {isEditing ? (
              <input
                type="text"
                className="input font-mono"
                value={formData.dni || ''}
                onChange={(e) => setFormData({ ...formData, dni: e.target.value.toUpperCase() })}
                placeholder="12345678A"
              />
            ) : (
              <p className="flex items-center gap-2 text-gray-900 dark:text-white">
                <CreditCard className="h-4 w-4 text-gray-400" />
                <span className="font-mono">{employee.dni}</span>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Email
            </label>
            {isEditing ? (
              <input
                type="email"
                className="input"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value || null })}
                placeholder="empleado@example.com"
              />
            ) : employee.email ? (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900 dark:text-white">{employee.email}</span>
                {employee.emailVerified ? (
                  <span className="text-xs bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400 px-2 py-0.5 rounded-full">
                    Verificado
                  </span>
                ) : (
                  <span className="text-xs bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400 px-2 py-0.5 rounded-full">
                    Pendiente
                  </span>
                )}
              </div>
            ) : (
              <p className="text-gray-400 dark:text-gray-500 italic">
                Sin email configurado
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Nombre
            </label>
            {isEditing ? (
              <input
                type="text"
                className="input"
                value={formData.firstName || ''}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            ) : (
              <p className="text-gray-900 dark:text-white">{employee.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Apellidos
            </label>
            {isEditing ? (
              <input
                type="text"
                className="input"
                value={formData.lastName || ''}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            ) : (
              <p className="text-gray-900 dark:text-white">{employee.lastName}</p>
            )}
          </div>
        </div>
      </div>

      {/* Work Info */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Información Laboral
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Puesto
            </label>
            {isEditing ? (
              <select
                className="input"
                value={formData.positionId || ''}
                onChange={(e) => setFormData({ ...formData, positionId: e.target.value || null })}
              >
                <option value="">Sin puesto asignado</option>
                {positions.map((position) => (
                  <option key={position.id} value={position.id}>{position.name}</option>
                ))}
              </select>
            ) : (
              <p className="flex items-center gap-2 text-gray-900 dark:text-white">
                <User className="h-4 w-4 text-gray-400" />
                {employee.position || 'Sin puesto asignado'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Centro de Trabajo
            </label>
            {isEditing ? (
              <select
                className="input"
                value={formData.workCenterType || ''}
                onChange={(e) => setFormData({ ...formData, workCenterType: e.target.value as WorkCenterType })}
              >
                {Object.entries(WORK_CENTER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            ) : (
              <p className="flex items-center gap-2 text-gray-900 dark:text-white">
                <Building2 className="h-4 w-4 text-gray-400" />
                {employee.workCenterName || WORK_CENTER_LABELS[employee.workCenterType]}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Turno
            </label>
            {isEditing ? (
              <select
                className="input"
                value={formData.shift || ''}
                onChange={(e) => setFormData({ ...formData, shift: e.target.value as Shift })}
              >
                {Object.entries(SHIFT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            ) : (
              <p className="flex items-center gap-2 text-gray-900 dark:text-white">
                <Clock className="h-4 w-4 text-gray-400" />
                {SHIFT_LABELS[employee.shift]}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Fecha de Alta
            </label>
            <p className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Calendar className="h-4 w-4 text-gray-400" />
              {formatDate(employee.startDate)} ({employee.yearsOfService} años)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Días a 5 Relevos
            </label>
            {isEditing ? (
              <input
                type="number"
                className="input"
                min="0"
                value={formData.daysAt5Shifts || 0}
                onChange={(e) => setFormData({ ...formData, daysAt5Shifts: parseInt(e.target.value) || 0 })}
              />
            ) : (
              <p className="text-xl font-bold text-primary-600">
                {employee.daysAt5Shifts.toLocaleString()}
              </p>
            )}
          </div>

          {isEditing && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Días usados PP5R
                </label>
                <input
                  type="number"
                  className="input"
                  min="0"
                  value={formData.daysUsedPP5R || 0}
                  onChange={(e) => setFormData({ ...formData, daysUsedPP5R: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                    checked={formData.hasStop || false}
                    onChange={(e) => setFormData({ ...formData, hasStop: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Tiene parada</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                    checked={formData.isPresecadoCelulosa || false}
                    onChange={(e) => setFormData({ ...formData, isPresecadoCelulosa: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Presecado Celulosa</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                    checked={formData.canRequestPP5R || false}
                    onChange={(e) => setFormData({ ...formData, canRequestPP5R: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Puede PP5R</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                    checked={formData.isActive !== false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Activo</span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Additional Details */}
      {!isEditing && (
        <div className="card lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Detalles Adicionales
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Parada</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {employee.hasStop ? 'Sí' : 'No'}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Presecado Celulosa</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {employee.isPresecadoCelulosa ? 'Sí' : 'No'}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Estado</p>
              <p className={`text-lg font-semibold mt-1 ${employee.isActive ? 'text-success-600' : 'text-error-600'}`}>
                {employee.isActive ? 'Activo' : 'Inactivo'}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Antigüedad</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {employee.yearsOfService} años
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Puede PP5R</p>
              <p className={`text-lg font-semibold mt-1 ${employee.canRequestPP5R ? 'text-success-600' : 'text-error-600'}`}>
                {employee.canRequestPP5R ? 'Sí' : 'No'}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Días usados PP5R</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {employee.daysUsedPP5R}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
